import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    Heart, Zap, Brain, Plane, Users, Mountain,
    ArrowLeft, Sparkles, Shield, Trophy, Compass
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Seo } from '../components/seo/Seo';
import { FaqSection } from '../components/seo/FaqSection';
import { FAQS } from '../components/seo/faqData';

// Nanouk Images
const NANOUK = {
    hero: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    happy: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    smart: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
};

export default function MeetNanouk() {
    const navigate = useNavigate();
    const { language } = useLanguage();
    
    const t = {
        de: {
            back: 'Zurück',
            title: 'Lerne Nanouk kennen',
            subtitle: 'Dein treuer Wellness-Begleiter',
            
            // Hero Section
            heroTitle: 'Der ultimative Teamplayer',
            heroQuote: '"Wir ziehen den Schlitten gemeinsam durch den Alltag."',
            heroDescription: 'Nanouk ist ein Siberian Husky – das Symbol für unermüdliche Energie und Teamarbeit. Er ist darauf programmiert, weite Strecken zurückzulegen und niemals aufzugeben.',
            
            // Traits
            traitsTitle: 'Nanouks Eigenschaften',
            traits: [
                {
                    icon: Zap,
                    title: 'Unermüdliche Energie',
                    description: 'Wie ein echter Schlittenhund hat Nanouk grenzenlose Ausdauer. Er motiviert dich, weiterzumachen – auch wenn der innere Schweinehund bellt.',
                    color: 'from-amber-500 to-orange-500'
                },
                {
                    icon: Users,
                    title: 'Echter Teamplayer',
                    description: 'Huskys sind Rudeltiere – sie wollen nicht allein sein. Nanouk begleitet dich durch jeden Tag, feiert deine Erfolge und tröstet bei Rückschlägen.',
                    color: 'from-blue-500 to-cyan-500'
                },
                {
                    icon: Brain,
                    title: 'Cleverer Kopf',
                    description: 'Huskys sind bekannt für ihre Intelligenz und Problemlösungsfähigkeit. Nanouk analysiert deine Daten und gibt dir smarte, personalisierte Tipps.',
                    color: 'from-violet-500 to-purple-500'
                },
                {
                    icon: Plane,
                    title: 'Reiselustig',
                    description: 'Ob Polarkreis oder Tropen – Huskys passen sich an. Nanouk hilft dir bei Jetlag, Zeitumstellung und hält dich auch auf Reisen fit.',
                    color: 'from-emerald-500 to-teal-500'
                },
                {
                    icon: Mountain,
                    title: 'Naturverbunden',
                    description: 'Huskys lieben die Wildnis. Nanouk erinnert dich daran, rauszugehen, frische Luft zu schnappen und die Natur zu genießen.',
                    color: 'from-green-500 to-lime-500'
                },
                {
                    icon: Heart,
                    title: 'Bedingungslose Treue',
                    description: 'Ein Husky gibt niemals auf. Nanouk bleibt an deiner Seite – egal ob du gerade 10km läufst oder auf der Couch entspannst.',
                    color: 'from-rose-500 to-pink-500'
                }
            ],
            
            // Buddy Vibe
            vibeTitle: 'Der Buddy-Vibe',
            vibeDescription: 'Nanouk ist der treue, loyale Kumpel, der dich morgens mit der Leine im Mund (metaphorisch) zum Sport motiviert. Er freut sich über jeden kleinen Fortschritt und wedelt mit dem Schwanz, wenn du deine Ziele erreichst.',
            
            // What Nanouk Does
            whatTitle: 'Was Nanouk für dich tut',
            whatItems: [
                { icon: Sparkles, text: 'Motiviert dich mit positiven Nachrichten' },
                { icon: Trophy, text: 'Feiert jeden Erfolg mit dir' },
                { icon: Compass, text: 'Gibt dir Orientierung im Gesundheits-Dschungel' },
                { icon: Shield, text: 'Erkennt seine Grenzen und verweist an Experten' }
            ],
            
            // CTA
            ctaTitle: 'Bereit für das Abenteuer?',
            ctaDescription: 'Nanouk wartet auf dich. Gemeinsam schaffen wir alles!',
            ctaButton: 'Jetzt starten'
        },
        en: {
            back: 'Back',
            title: 'Meet Nanouk',
            subtitle: 'Your loyal wellness companion',
            
            heroTitle: 'The Ultimate Team Player',
            heroQuote: '"We pull the sled through everyday life together."',
            heroDescription: 'Nanouk is a Siberian Husky – the symbol of tireless energy and teamwork. He is programmed to cover long distances and never give up.',
            
            traitsTitle: "Nanouk's Traits",
            traits: [
                {
                    icon: Zap,
                    title: 'Tireless Energy',
                    description: 'Like a real sled dog, Nanouk has boundless endurance. He motivates you to keep going – even when your inner couch potato is calling.',
                    color: 'from-amber-500 to-orange-500'
                },
                {
                    icon: Users,
                    title: 'True Team Player',
                    description: "Huskies are pack animals – they don't want to be alone. Nanouk accompanies you through every day, celebrates your successes and comforts you during setbacks.",
                    color: 'from-blue-500 to-cyan-500'
                },
                {
                    icon: Brain,
                    title: 'Clever Mind',
                    description: 'Huskies are known for their intelligence and problem-solving skills. Nanouk analyzes your data and gives you smart, personalized tips.',
                    color: 'from-violet-500 to-purple-500'
                },
                {
                    icon: Plane,
                    title: 'Travel Enthusiast',
                    description: 'Whether Arctic or tropics – Huskies adapt. Nanouk helps you with jet lag, time changes and keeps you fit while traveling.',
                    color: 'from-emerald-500 to-teal-500'
                },
                {
                    icon: Mountain,
                    title: 'Nature Lover',
                    description: 'Huskies love the wilderness. Nanouk reminds you to go outside, breathe fresh air and enjoy nature.',
                    color: 'from-green-500 to-lime-500'
                },
                {
                    icon: Heart,
                    title: 'Unconditional Loyalty',
                    description: 'A Husky never gives up. Nanouk stays by your side – whether you are running 10km or relaxing on the couch.',
                    color: 'from-rose-500 to-pink-500'
                }
            ],
            
            vibeTitle: 'The Buddy Vibe',
            vibeDescription: 'Nanouk is the loyal, faithful buddy who motivates you to exercise in the morning with a leash in his mouth (metaphorically). He is happy about every little progress and wags his tail when you reach your goals.',
            
            whatTitle: 'What Nanouk Does For You',
            whatItems: [
                { icon: Sparkles, text: 'Motivates you with positive messages' },
                { icon: Trophy, text: 'Celebrates every success with you' },
                { icon: Compass, text: 'Guides you through the health jungle' },
                { icon: Shield, text: 'Knows his limits and refers to experts' }
            ],
            
            ctaTitle: 'Ready for the Adventure?',
            ctaDescription: 'Nanouk is waiting for you. Together we can do anything!',
            ctaButton: 'Get Started'
        }
    }[language] || {};

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-950">
            <Seo
                title={language === 'de' ? 'Lerne Nanouk kennen – dein Husky KI-Coach' : 'Meet Nanouk – Your Husky AI Coach'}
                description={language === 'de'
                    ? 'Nanouk ist der Husky und KI-Coach von BESTBUDDY: Er analysiert deine Wellness-Daten, motiviert dich täglich und feiert deine Fortschritte.'
                    : 'Nanouk is the husky and AI coach of BESTBUDDY: he analyzes your wellness data, motivates you daily and celebrates your progress.'}
                path={language === 'en' ? '/en/meet-nanouk' : '/meet-nanouk'}
                language={language}
                withHreflang
            />
            {/* Floating snowflakes effect */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-white/20 rounded-full"
                        initial={{ 
                            x: Math.random() * window.innerWidth,
                            y: -20 
                        }}
                        animate={{ 
                            y: window.innerHeight + 20,
                            x: Math.random() * window.innerWidth
                        }}
                        transition={{
                            duration: 10 + Math.random() * 10,
                            repeat: Infinity,
                            delay: Math.random() * 5
                        }}
                    />
                ))}
            </div>

            {/* Back Button */}
            <div className="absolute top-6 left-6 z-20">
                <Button 
                    variant="ghost" 
                    onClick={() => navigate(-1)}
                    className="text-foreground/70 hover:text-foreground hover:bg-white/10"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t.back}
                </Button>
            </div>

            {/* Hero Section */}
            <section className="relative pt-24 pb-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="relative inline-block mb-8"
                    >
                        {/* Glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-violet-500/30 rounded-full blur-3xl" />
                        
                        {/* Nanouk Image */}
                        <motion.img
                            src={NANOUK.hero}
                            alt="Nanouk"
                            className="relative w-48 h-48 md:w-64 md:h-64 object-contain drop-shadow-2xl rounded-full"
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 3, repeat: Infinity }}
                        />
                        
                        {/* Paw prints */}
                        <motion.div
                            className="absolute -bottom-4 -left-4 text-2xl"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                        >
                            🐾
                        </motion.div>
                        <motion.div
                            className="absolute -bottom-4 -right-4 text-2xl"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                        >
                            🐾
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        <Badge className="mb-4 bg-gradient-to-r from-cyan-500 to-violet-500 text-foreground border-0">
                            Siberian Husky 🐺
                        </Badge>
                        
                        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4">
                            {t.title}
                        </h1>
                        <p className="text-xl text-cyan-300 mb-6">
                            {t.subtitle}
                        </p>
                        
                        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto border border-white/10">
                            <h2 className="text-2xl font-bold text-foreground mb-2">
                                {t.heroTitle}
                            </h2>
                            <p className="text-lg text-cyan-200 italic mb-4">
                                {t.heroQuote}
                            </p>
                            <p className="text-foreground/70">
                                {t.heroDescription}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Traits Section */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.h2 
                        className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                    >
                        {t.traitsTitle}
                    </motion.h2>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {t.traits?.map((trait, index) => (
                            <motion.div
                                key={index}
                                initial={{ y: 30, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all group"
                            >
                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${trait.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                    <trait.icon className="w-6 h-6 text-foreground" />
                                </div>
                                <h3 className="text-xl font-bold text-foreground mb-2">
                                    {trait.title}
                                </h3>
                                <p className="text-foreground/60 text-sm leading-relaxed">
                                    {trait.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Buddy Vibe Section */}
            <section className="py-16 px-6 bg-gradient-to-r from-violet-900/30 to-cyan-900/30">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="text-center"
                    >
                        <div className="flex justify-center mb-6">
                            <motion.img
                                src={NANOUK.happy}
                                alt="Happy Nanouk"
                                className="w-32 h-32 object-contain"
                                animate={{ rotate: [-5, 5, -5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            />
                        </div>
                        
                        <h2 className="text-3xl font-bold text-foreground mb-4">
                            {t.vibeTitle}
                        </h2>
                        <p className="text-lg text-foreground/70 leading-relaxed max-w-2xl mx-auto">
                            {t.vibeDescription}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* What Nanouk Does Section */}
            <section className="py-16 px-6">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-foreground text-center mb-12">
                        {t.whatTitle}
                    </h2>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                        {t.whatItems?.map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ x: index % 2 === 0 ? -30 : 30, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10"
                            >
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                                    <item.icon className="w-5 h-5 text-foreground" />
                                </div>
                                <span className="text-foreground/80">{item.text}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="max-w-2xl mx-auto text-center"
                >
                    <div className="bg-gradient-to-r from-violet-600/20 to-cyan-600/20 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                        <motion.div
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="text-5xl mb-4"
                        >
                            🐺
                        </motion.div>
                        
                        <h2 className="text-3xl font-bold text-foreground mb-3">
                            {t.ctaTitle}
                        </h2>
                        <p className="text-foreground/60 mb-6">
                            {t.ctaDescription}
                        </p>
                        
                        <Button 
                            size="lg"
                            onClick={() => navigate('/auth')}
                            className="bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-600 hover:to-violet-600 text-foreground px-8 py-6 text-lg rounded-xl"
                        >
                            {t.ctaButton}
                        </Button>
                    </div>
                </motion.div>
            </section>

            {/* FAQ */}
            <section className="py-16 px-6">
                <div className="max-w-3xl mx-auto rounded-3xl bg-white/5 border border-white/10 p-6 text-white [&_h2]:text-white [&_button]:text-white [&_.text-muted-foreground]:text-white/60">
                    <FaqSection faqs={FAQS.nanouk[language]} language={language} />
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 text-center text-foreground/40 text-sm">
                <p>BESTBUDDY © 2025 • Made with 💙 for your wellness</p>
            </footer>
        </div>
    );
}
